import React from "react"
import L10n from "lib/carrot-l10n"
import { AuthButton, AuthCard, AuthLayout } from "components/views/login/Shared"
import { useFormContext } from "react-hook-form"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { FlexRowCenter, FlexColumnCenter, HR, Spacer } from "components/views/atoms/Atoms"
import { Body, BodySmall } from "components/views/atoms/Bodies"
import { ArrowLink } from "components/views/atoms/links/ArrowLink"
import ErrorMessageMarkdown from "components/views/login/ErrorMessageMarkdown"
import { useSignUp } from "./context/SignUpContext"
import { TextLink } from "../views/atoms/links/TextLink"
import { H1 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const EmailInput = ({ enabled, email }) => {
  const { register, errors } = useFormContext()

  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Email is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={email}
      id="email"
      name="email"
      label="Email"
      type="email"
      autoFocus
    />
  )
}

function SignUpHeader() {
  const { getValues } = useFormContext()
  const { errorMessage, showTryAgain } = useSignUp()

  if (errorMessage) {
    return <ErrorMessageMarkdown source={errorMessage} />
  }

  if (showTryAgain) {
    return (
      <>
        <FlexRowCenter>
          <H1>Try again</H1>
        </FlexRowCenter>
        <FlexColumnCenter className="tc">
          <Spacer height={0.5} />
          <Body color="black-80">{l`Sorry, we couldn’t find your account with the email address you provided.`}</Body>
          <Spacer height={1.5} />
          <Body color="black-80">
            {l`Try another email address you think your company could have on file for you or`}&nbsp;
            <TextLink to={`/signup-find-account?email=${encodeURIComponent(getValues("email"))}`}>
              {l`find your account`}
            </TextLink>
            {l` using your name and date of birth.`}
          </Body>
        </FlexColumnCenter>
      </>
    )
  }

  return (
    <>
      <FlexRowCenter>
        <H1>{l`client.signUp.long`}</H1>
      </FlexRowCenter>
      <FlexColumnCenter className="tc">
        <Spacer height={0.5} />
        <Body color="black-80">{l`Sign up with your company email address or the`}</Body>
        <Body color="black-80">{l`address your company uses for you.`}</Body>
      </FlexColumnCenter>
    </>
  )
}

const SignUpButton = ({ showSubmittingLabel, onClick }) => (
  <div className="flex flex-auto justify-center">
    <AuthButton role="button" onClick={onClick}>
      {showSubmittingLabel ? l`client.signUp.submitting` : l`client.signUp.short`}
    </AuthButton>
  </div>
)

const SignUpFooter = () => (
  <>
    <Spacer height={1.5} />
    <HR color="light-gray" />
    <Spacer height={0.75} />
    <div className="flex flex-auto justify-center">
      <span className="pr3">
        <BodySmall color="black-80">{l`client.signUp.alreadyHaveAnAccount`}</BodySmall>
      </span>
      <ArrowLink to="/">{l`client.login`}</ArrowLink>
    </div>
  </>
)

const SignUpForm = () => {
  const { shake, inputsEnabled, showSubmittingLabel, username, onSubmit } = useSignUp()

  return (
    <AuthCard shake={shake} onSubmit={onSubmit}>
      <Spacer height={4} />
      <SignUpHeader />
      <Spacer height={1.5} />
      <EmailInput enabled={inputsEnabled} email={username} />
      <Spacer height={1.5} />
      <SignUpButton onClick={onSubmit} showSubmittingLabel={showSubmittingLabel} />
      <SignUpFooter />
    </AuthCard>
  )
}

const SignUpPage = () => {
  return (
    <AuthLayout title={l`client.signUp.short`}>
      <SignUpForm />
    </AuthLayout>
  )
}

export default SignUpPage
